import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Crown, Plus, Trash2 } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import { Input } from "@v2/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { usePermissions } from "../../lib/permissions";
import { EmptyState, SectionLoading } from "../../components/state";
import type { TeamMemberRole } from "../../types";

type Member = {
  membership_id: string;
  user_id: string;
  email: string | null;
  role: TeamMemberRole;
  status: string;
  player_id: string | null;
  player_display_name: string | null;
  player_ea_id: string | null;
};

export function TeamMembersManager({ teamId }: { teamId: string }) {
  const { t } = useTranslation("settings");
  const queryClient = useQueryClient();
  const { role: callerRole, isAppAdmin } = usePermissions();
  const isCallerFounder = callerRole === "founder" || isAppAdmin;
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamMemberRole>("coach");
  const [error, setError] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<Member | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);

  const membersQuery = useQuery({
    queryKey: ["team-members", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_team_members", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as Member[];
    },
  });

  const invite = useMutation({
    mutationFn: async () => {
      const { error: rpcError } = await supabaseV2.rpc("invite_team_member", {
        _team_id: teamId,
        _email: email.trim(),
        _role: role,
      });
      if (rpcError) throw rpcError;
    },
    onSuccess: () => {
      setEmail("");
      setRole("coach");
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["team-members", teamId] });
    },
    onError: (e: any) => setError(e.message || String(e)),
  });

  const updateRole = useMutation({
    mutationFn: async ({ id, nextRole }: { id: string; nextRole: TeamMemberRole }) => {
      const { error } = await supabaseV2.rpc("update_team_membership_role", {
        _membership_id: id,
        _role: nextRole,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team-members", teamId] }),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabaseV2.rpc("remove_team_member", { _membership_id: id });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["team-members", teamId] }),
  });

  const transferOwnership = useMutation({
    mutationFn: async (membershipId: string) => {
      const { error } = await supabaseV2.rpc("transfer_team_ownership", {
        _membership_id: membershipId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members", teamId] });
      queryClient.invalidateQueries({ queryKey: ["memberships"] });
      setTransferTarget(null);
      setTransferError(null);
    },
    onError: (e: any) => setTransferError(e.message || String(e)),
  });

  const members = membersQuery.data || [];

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t("team.members.title")}</CardTitle>
        <CardDescription>
          {t("team.members.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <form
          className="flex flex-wrap gap-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!email.trim()) return;
            invite.mutate();
          }}
        >
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("team.members.emailPh")}
            className="h-8 flex-1 min-w-[200px] text-sm"
            required
          />
          <Select value={role} onValueChange={(v) => setRole(v as TeamMemberRole)}>
            <SelectTrigger className="h-8 w-[150px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="founder">{t("team.members.roleFounder")}</SelectItem>
              <SelectItem value="coach">{t("team.members.roleCoach")}</SelectItem>
              <SelectItem value="captain">{t("team.members.roleCaptain")}</SelectItem>
              <SelectItem value="player">{t("team.members.rolePlayer")}</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" size="sm" className="h-8" disabled={invite.isPending || !email.trim()}>
            <Plus className="h-3 w-3" /> {t("team.members.invite")}
          </Button>
        </form>
        {error && (
          <div className="rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {error}
          </div>
        )}
        {membersQuery.isLoading ? (
          <SectionLoading />
        ) : members.length === 0 ? (
          <EmptyState text={t("team.members.empty")} />
        ) : (
          <div className="rounded-2xl bg-background overflow-hidden">
            {members.map((m, idx) => {
              const primaryLabel =
                m.player_display_name || m.email || m.user_id.slice(0, 8) + "…";
              const subLabel = m.player_display_name && m.email ? m.email : undefined;
              return (
                <div
                  key={m.membership_id}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm",
                    idx > 0 && "border-t"
                  )}
                >
                  <span className="flex-1 min-w-0">
                    <span className="font-medium truncate block">{primaryLabel}</span>
                    <span className="text-xs text-muted-foreground font-mono truncate block">
                      {subLabel || ""}
                      {m.player_ea_id ? `${subLabel ? " · " : ""}EA ${m.player_ea_id}` : ""}
                    </span>
                  </span>
                  <Select
                    value={m.role}
                    onValueChange={(v) => updateRole.mutate({ id: m.membership_id, nextRole: v as TeamMemberRole })}
                  >
                    <SelectTrigger className="h-7 w-[130px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="founder">{t("team.members.roleFounder")}</SelectItem>
                      <SelectItem value="coach">{t("team.members.roleCoach")}</SelectItem>
                      <SelectItem value="captain">{t("team.members.roleCaptain")}</SelectItem>
                      <SelectItem value="player">{t("team.members.rolePlayer")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {isCallerFounder && m.role !== "founder" && m.status === "active" && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-amber-600 hover:bg-amber-50"
                      title={t("team.members.transferTooltip")}
                      onClick={() => {
                        setTransferError(null);
                        setTransferTarget(m);
                      }}
                    >
                      <Crown className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                    onClick={() => {
                      if (window.confirm(t("team.members.removeConfirm"))) remove.mutate(m.membership_id);
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={!!transferTarget} onOpenChange={(o) => !o && setTransferTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("team.members.transferTitle")}</DialogTitle>
            <DialogDescription>
              {t("team.members.transferDesc")}
            </DialogDescription>
          </DialogHeader>
          {transferTarget && (
            <div className="rounded-2xl bg-background px-3 py-2 text-sm">
              <p className="font-semibold">
                {transferTarget.player_display_name || transferTarget.email || transferTarget.user_id.slice(0, 8) + "…"}
              </p>
              {transferTarget.email && transferTarget.player_display_name && (
                <p className="text-xs text-muted-foreground font-mono">{transferTarget.email}</p>
              )}
            </div>
          )}
          {transferError && (
            <div className="rounded-md px-2 py-1 text-xs bg-rose-50 text-rose-700 border border-rose-200">
              {transferError}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferTarget(null)} disabled={transferOwnership.isPending}>
              {t("team.members.cancel")}
            </Button>
            <Button
              variant="default"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => transferTarget && transferOwnership.mutate(transferTarget.membership_id)}
              disabled={transferOwnership.isPending}
            >
              <Crown />
              {transferOwnership.isPending ? t("team.members.transferring") : t("team.members.transferConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
